import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Eye, EyeOff, Lock, User, LogIn, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Separator } from '../../components/ui/separator';
import { useAuth, LoginCredentials } from '../../hooks/useAuth';
import { useAuthContext } from '../../contexts/AuthContext';
import { API_BASE_URL } from '../../config/api';
import { useTranslation } from 'react-i18next';
import AuthLayout from '../../layouts/AuthLayout';

const Login: React.FC = () => {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, loading } = useAuth();
  const { isAuthenticated } = useAuthContext();
  
  const [formData, setFormData] = useState<LoginCredentials>({
    email: '',
    password: '',
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Partial<LoginCredentials>>({});
  const [loginError, setLoginError] = useState<string | null>(null);

  // Check for SSO error in URL params
  useEffect(() => {
    const error = searchParams.get('error');
    if (error) {
      setLoginError(t('login.error_sso'));
    }
  }, [searchParams, t]);

  // Debug logging for auth state
  useEffect(() => {
    console.log('Auth state changed:', { isAuthenticated });
    if (isAuthenticated) {
      console.log('User is authenticated, redirecting to dashboard');
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear errors when user types
    if (validationErrors[name as keyof LoginCredentials]) {
      setValidationErrors(prev => ({ ...prev, [name]: undefined }));
    }
    setLoginError(null);
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<LoginCredentials> = {};
    
    if (!formData.email.trim()) {
      newErrors.email = t('login.error_email_required');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('login.error_email_invalid');
    }
    
    if (!formData.password) {
      newErrors.password = t('login.error_password_required');
    }
    
    setValidationErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      console.log('Submitting login form...');
      const result = await login(formData);
      console.log('Login result:', result);
      
      if (result.isAuthenticated) {
        console.log('Login successful, redirecting...');
        
        // Check if there's a pending signing redirect
        const signingToken = sessionStorage.getItem('signing_redirect_token');
        if (signingToken) {
          sessionStorage.removeItem('signing_redirect_token');
          setTimeout(() => {
            window.location.href = window.location.origin + '/sign/' + signingToken;
          }, 100);
          return;
        }
        
        // Use the most direct way to redirect
        setTimeout(() => {
          window.location.href = window.location.origin + '/';
        }, 100);
      }
    } catch (error) {
      console.error('Login failed', error);
      setLoginError(t('login.error_failed'));
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  return (
    <AuthLayout>
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border dark:border-slate-700 p-6 sm:p-8">
        <h2 className="text-2xl font-bold text-center mb-1">{t('login.signin_button')}</h2>
        <p className="text-sm text-muted-foreground text-center mb-6">{t('login.subtitle')}</p>

        <div className="space-y-4">
            {/* Display login error */}
            {loginError && (
              <div className="bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300 p-3 rounded-md text-sm flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                {loginError}
              </div>
            )}

            {/* Email/Password Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <div className="absolute left-3 top-3 text-muted-foreground">
                  <User className="h-4 w-4" />
                </div>
                <Input
                  type="email"
                  name="email"
                  placeholder={t('login.email_placeholder')}
                  value={formData.email}
                  onChange={handleChange}
                  className={`pl-10 ${validationErrors.email ? 'border-red-500' : ''}`}
                  disabled={loading}
                />
                {validationErrors.email && (
                  <div className="text-red-500 text-xs mt-1 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {validationErrors.email}
                  </div>
                )}
              </div>
              <div className="relative">
                <div className="absolute left-3 top-3 text-muted-foreground">
                  <Lock className="h-4 w-4" />
                </div>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder={t('login.password_placeholder')}
                  value={formData.password}
                  onChange={handleChange}
                  className={`pl-10 ${validationErrors.password ? 'border-red-500' : ''}`}
                  disabled={loading}
                />
                <div
                  className="absolute right-3 top-3 text-muted-foreground cursor-pointer"
                  tabIndex={-1}
                  onClick={togglePasswordVisibility}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </div>
                {validationErrors.password && (
                  <div className="text-red-500 text-xs mt-1 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {validationErrors.password}
                  </div>
                )}
              </div>
              <div className="flex justify-end">
                <Link to="/forgot-password" className="text-xs text-primary hover:underline">
                  {t('login.forgot_password_link')}
                </Link>
              </div>
              <Button type="submit" className="w-full flex items-center gap-2" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4" />
                    {t('login.signing_in')}
                  </>
                ) : (
                  <>
                    <LogIn className="h-4 w-4" />
                    {t('login.signin_button')}
                  </>
                )}
              </Button>
            </form>

            {/* Separator */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <Separator className="w-full" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-card px-2 text-xs text-muted-foreground">
                  {t('login.email_password_toggle')}
                </span>
              </div>
            </div>

            {/* SSO Buttons */}
            <div className="grid grid-cols-2 gap-3">
              <Button variant="outline" className="w-full flex items-center gap-2 h-12 text-sm font-semibold" onClick={() => {
                const backendUrl = API_BASE_URL.replace(/\/api\/?$/, '');
                window.location.href = `${backendUrl}/auth/microsoft`;
              }}>
                <Microsoft className="h-5 w-5" />
                {t('login.microsoft_button')}
              </Button>

              <Button variant="outline" className="w-full flex items-center gap-2 h-12 text-sm font-semibold" onClick={() => {
                const backendUrl = API_BASE_URL.replace(/\/api\/?$/, '');
                window.location.href = `${backendUrl}/auth/google`;
              }}>
                <Google className="h-5 w-5" />
                {t('login.google_button')}
              </Button>
            </div>

            <p className="text-center text-sm text-muted-foreground">
              {t('login.signup_prompt')}{' '}
              <Link to="/join" className="text-primary hover:underline">{t('login.signup_link')}</Link>
            </p>
          </div>
        </div>
    </AuthLayout>
  );
};

export default Login;

function Microsoft(props: any) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
    >
      <path d="M11.4 2.8H2.8V11.4H11.4V2.8Z" fill="#F25022"/>
      <path d="M11.4 12.6H2.8V21.2H11.4V12.6Z" fill="#00A4EF"/>
      <path d="M21.2 2.8H12.6V11.4H21.2V2.8Z" fill="#7FBA00"/>
      <path d="M21.2 12.6H12.6V21.2H21.2V12.6Z" fill="#FFB900"/>
    </svg>
  );
}

function Google(props: any) {
  return (
    <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

function Loader2(props: any) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );
}
